import { useEffect, useState, useRef, Suspense } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import axios from 'axios';
import { isMobile } from 'react-device-detect';
import { useMortgageState } from '../../context/mortgage';
import { sleep } from '../../helpers';
import Skelet from '../Skelet';
import MortgageCarouselSkeletonItem from './MortgageCarouselSkeletonItem';
import styles from '../../styles/modules/Carousel.module.scss';

const MortgageCarouselItem = dynamic(() => import('./MortgageCarouselItem'), { suspense: true });

const MortgageCarousel = () => {
  const mortgageState = useMortgageState();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState([]);
  const [carouselScrollPosition, setCarouselScrollPosition] = useState(0);
  const [carouselScrollWidth, setCarouselScrollWidth] = useState(0);
  const [carouselElWidth, setCarouselElWidth] = useState(0);
  const carouselContentEl = useRef();

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        await sleep(300);

        const response = await axios.post(
          process.env.NEXT_PUBLIC_API + '/objects',
          {
            length: 11,
            type: 'продажа',
            category: 'квартира',
            priceFrom: mortgageState.objectPrice - 750000,
            priceTo: mortgageState.objectPrice + 750000
          },
          { signal }
        );

        if (!response.data || response.data.status !== 'ok') throw new Error(response.data.text);

        setData(response.data.data.data);
        setIsLoading(false);
        setIsError(false);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          setIsLoading(false);
          setIsError(true);
        }
      }
    };
    fetchData();

    if (carouselContentEl.current) {
      carouselContentEl.current.scrollTo({
        left: 0,
        behavior: 'auto'
      });
      setCarouselScrollPosition(0);
    }

    return () => controller.abort();
  }, [mortgageState]);

  const handlePreviousClick = () => {
    if (!carouselContentEl.current) return;

    carouselContentEl.current.scrollTo({
      left: carouselContentEl.current.scrollLeft - carouselContentEl.current.offsetWidth / 2,
      behavior: 'smooth'
    });

    setCarouselScrollPosition(
      carouselContentEl.current.scrollLeft - carouselContentEl.current.offsetWidth / 2
    );
    setCarouselScrollWidth(carouselContentEl.current.scrollWidth);
    setCarouselElWidth(carouselContentEl.current.offsetWidth);
  };
  const handleNextClick = () => {
    if (!carouselContentEl.current) return;

    carouselContentEl.current.scrollTo({
      left: carouselContentEl.current.scrollLeft + carouselContentEl.current.offsetWidth / 2,
      behavior: 'smooth'
    });

    setCarouselScrollPosition(
      carouselContentEl.current.scrollLeft + carouselContentEl.current.offsetWidth / 2
    );
    setCarouselScrollWidth(carouselContentEl.current.scrollWidth);
    setCarouselElWidth(carouselContentEl.current.offsetWidth);
  };

  if (isLoading || isError)
    return (
      <section className="block crop">
        <div className="fixed">
          <h3>
            <Skelet style={{ maxWidth: 400, width: '100%', height: 47.5 }} />
          </h3>
          <div className={styles.carousel}>
            <div className={styles.carouselContent}>
              {[...Array(6).keys()].map((value) => (
                <MortgageCarouselSkeletonItem key={value} />
              ))}
            </div>
          </div>
        </div>
      </section>
    );

  return !!data.length ? (
    <section className="block crop">
      <div className="fixed">
        <h3>Предложения под ипотеку</h3>
        <div className={styles.carousel}>
          {carouselScrollPosition > 0 && (
            <button
              className={[styles.carouselButton, styles.carouselButtonPrevious].join(' ')}
              onClick={handlePreviousClick}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}
          <div
            className={[styles.carouselContent, isMobile && styles.touch].filter(Boolean).join(' ')}
            ref={carouselContentEl}
          >
            {data.map((value, index) => (
              <Suspense fallback={<MortgageCarouselSkeletonItem />} key={index}>
                <MortgageCarouselItem item={value} />
              </Suspense>
            ))}
            <Link
              href={`/objects/buy/flats?priceFrom=${
                mortgageState.objectPrice - 750000 <= 0 ? '' : mortgageState.objectPrice - 750000
              }&priceTo=${mortgageState.objectPrice + 750000}`}
              passHref
              className={styles.carouselItemMore}
              type="button"
              target="_blank"
            >
              <span>Смотреть больше вариантов</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100"
                height="100"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
            <div className={styles.carouselItemDummy} />
          </div>
          {carouselScrollPosition + carouselElWidth <= carouselScrollWidth && data.length >= 4 && (
            <button
              className={[styles.carouselButton, styles.carouselButtonNext].join(' ')}
              onClick={handleNextClick}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </section>
  ) : null;
};

export default MortgageCarousel;
